import { Injectable } from '@angular/core';
import {ExportToCsv} from 'export-to-csv';
import _ from 'lodash';
import { CurrencyPipe, DatePipe, TitleCasePipe } from '@angular/common';
import { MatTableDataSource } from '@angular/material/table';
import { saveAs } from 'file-saver';

export interface CsvColumn<T> {
  header: string;
  value: (row: T) => unknown;
}

// Match the application's default en-US Angular pipes, including null values.
export const csvDate = (value: any, format: string, timezone?: string): string | null =>
  new DatePipe('en-US').transform(value, format, timezone);
export const csvCurrency = (value: any): string | null => new CurrencyPipe('en-US').transform(value);
export const csvTitleCase = (value: string): string => new TitleCasePipe().transform(value);
export const csvText = (value: unknown): string => value == null ? '' : String(value);


@Injectable({
  providedIn: 'root'
})
export class ExporttocsvService {

  constructor() { }

  /** Export only loaded rows, applying the table's public filter/sort contract.
   * Client pagination does not limit the export; no server pages are fetched.
   */
  tableRows<T>(source: MatTableDataSource<T> | readonly T[]): T[] {
    if (Array.isArray(source)) return [...source];
    const table = source as MatTableDataSource<T>;
    const rows = table.data.filter(row => !table.filter || table.filterPredicate(row, table.filter));
    return table.sort ? table.sortData(rows, table.sort) : rows;
  }

  exportTable<T>(title: string, source: MatTableDataSource<T> | readonly T[],
                 definitions: Record<string, CsvColumn<T>>, order: readonly string[],
                 options: {exclude?: readonly string[]; headers?: boolean; footer?: readonly unknown[]} = {}): void {
    const columns = order.filter(id => !options.exclude?.includes(id)).map(id => {
      if (!definitions[id]) throw new Error(`Missing CSV column definition: ${id}`);
      return definitions[id];
    });
    const rows = this.tableRows(source).map(row => columns.map(column => column.value(row)));
    const matrix: unknown[][] = options.headers === false ? rows : [columns.map(column => column.header), ...rows];
    if (rows.length && options.footer) matrix.push([...options.footer]);
    this.downloadRows(title, matrix, columns.length);
  }

  /** Treat headers as data so the existing serializer also escapes header quotes,
   * commas, and newlines, and permits duplicate/blank header labels.
   */
  serializeRows(rows: readonly (readonly unknown[])[], width: number): string {
    if (width === 0) throw new Error('CSV export requires at least one column');
    const keys = Array.from({length: width}, (_, index) => String(index));
    const records = rows.map(row => {
      if (row.length !== width) throw new Error('CSV row does not match its column definitions');
      return row.reduce<Record<string, string>>((record, value, index) => {
        record[keys[index]] = csvText(value);
        return record;
      }, {});
    });
    const serializer = new ExportToCsv({headers: keys, useKeysAsHeaders: false, showLabels: true,
      showTitle: false, useBom: false, quoteStrings: '"', fieldSeparator: ',', decimalSeparator: '.'});
    const csv = serializer.generateCsv(records, true) as unknown as string;
    // Remove only the synthetic numeric-key header emitted by export-to-csv.
    return '\ufeff' + csv.slice(csv.indexOf('\r\n') + 2);
  }

  downloadRows(title: string, rows: readonly (readonly unknown[])[], width: number): void {
    saveAs(new Blob([this.serializeRows(rows, width)], {type: 'text/csv;charset=utf-8'}), `${title}.csv`);
  }

  replaceUndefinedWithEmptyString(obj: any): any {
    return _.transform(obj, (result, value, key) => {
      result[key] = this.normalizeCsvValue(value);
      if (value instanceof Date) {
        result[key] = result[key].split('T')[0];
      }
      // If the value is an object, apply the transformation recursively
      if (_.isObject(value) && !_.isArray(value)) {
        result[key] = this.replaceUndefinedWithEmptyString(value);
      }
    });
  }

  private normalizeCsvValue(value: any): string {
    if (_.isUndefined(value) || _.isNull(value)) {
      return '';
    }

    const text = value.toString();
    return text.trim().toLowerCase() === 'null' ? '' : text;
  }

  export(title: string, data: any): Promise<any> {
    console.log('EXPORT', data)
    const options = {
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      showTitle: false,
      filename: title,
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true,
    };
    const csvExporter = new ExportToCsv(options);
    return csvExporter.generateCsv(this.replaceUndefinedWithEmptyString(data));
  }

}
